import { useState, useEffect } from "react";
import { createCost } from "../../api/contract/cost";

const ExcelDemoAnimation = ({ active }) => {
  const [step, setStep] = useState(0);
  const [fakeInput, setFakeInput] = useState("");

  const excelText =
    "A001\t본공사\t2025-01-05\t150000\t130000\t메모내용\n" +
    "A002\t전기공사\t2025-01-06\t200000\t180000\t비고";

  useEffect(() => {
    if (!active) return;

    setStep(0);
    setFakeInput("");

    const timers = [
      setTimeout(() => setStep(1), 800), // 선택
      setTimeout(() => setStep(2), 1800), // 복사
      setTimeout(() => setStep(3), 2800), // textarea 활성
      setTimeout(() => {
        // 붙여넣기
        setFakeInput(excelText);
        setStep(4);
      }, 3800),
      setTimeout(() => setStep(5), 4800), // 테이블 생성

      // ⭐ 애니메이션 종료 → 빈 화면
      setTimeout(() => {
        setStep(null);
        setFakeInput("");
      }, 7000),
    ];

    return () => timers.forEach(clearTimeout);
  }, [active]);

  const excelData = [
    ["A001", "본공사", "2025-01-05", "150000", "130000", "메모내용"],
    ["A002", "전기공사", "2025-01-06", "200000", "180000", "비고"],
  ];

  return (
    <div className="select-none">
      {/* Excel 영역 */}
      {step !== null && (
        <div className="border rounded bg-white p-2 inline-block shadow relative mb-3">
          <div className="grid grid-cols-7">
            <div></div>
            {["A", "B", "C", "D", "E", "F"].map((h) => (
              <div
                key={h}
                className="border bg-gray-100 px-2 py-1 text-[10px] text-center"
              >
                {h}
              </div>
            ))}
          </div>

          {excelData.map((row, rIdx) => (
            <div key={rIdx} className="grid grid-cols-7">
              <div className="border bg-gray-100 px-2 py-1 text-[10px] text-center">
                {rIdx + 1}
              </div>

              {row.map((cell, cIdx) => (
                <div
                  key={cIdx}
                  className={`
                    border px-2 py-1 text-xs bg-gray-50
                    transition-all
                    ${step === 1 ? "outline outline-2 outline-blue-400" : ""}
                    ${step === 2 ? "bg-blue-100" : ""}
                    `}
                >
                  {cell}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* textarea 붙여넣기 */}
      {step !== null && (
        <textarea
          className={`w-full h-16 border rounded p-2 text-xs transition-all bg-gray-50 ${
            step === 3 ? "animate-pulse" : ""
          }`}
          value={fakeInput}
          readOnly
          placeholder="붙여넣기 예시"
        />
      )}

      {/* 예시 테이블 생성 */}
      {step >= 5 && (
        <div className="mt-3 border rounded shadow overflow-hidden animate-[fadeInSlide_.6s_ease-out_forwards] opacity-0">
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                {["코드", "공종", "지불일", "계획금액", "실제금액", "메모"].map(
                  (h) => (
                    <th key={h} className="border px-2 py-1">
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {excelData.map((row, i) => (
                <tr
                  key={i}
                  className="opacity-0 animate-[fadeInRow_.5s_.4s_forwards]"
                >
                  {row.map((cell, j) => (
                    <td key={j} className="border px-2 py-1">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const CostBulkNew = ({ isOpen, onClose, onCreated }) => {
  const [rawText, setRawText] = useState("IPC-25-03-RC,철근콘크리트 공사,2025-03-25,850000000,830000000,메모");
  const [rows, setRows] = useState([]);

  const [demoKey, setDemoKey] = useState(0);

  const restartExample = () => setDemoKey((prev) => prev + 1);

  const validateRow = (row) => {
    const errors = {};

    if (!row.code) errors.code = "코드가 비어있습니다.";

    if (!/^\d{4}-\d{2}-\d{2}$/.test(row.payment_date)) {
      errors.payment_date = "날짜 형식이 올바르지 않습니다 (YYYY-MM-DD)";
    }

    if (row.expected_amount && !/^\d+$/.test(row.expected_amount)) {
      errors.expected_amount = "숫자만 입력하세요";
    }
    if (row.actual_amount && !/^\d+$/.test(row.actual_amount)) {
      errors.actual_amount = "숫자만 입력하세요";
    }

    return errors;
  };

  const parseExcel = (text) => {
    const lines = text.trim().split("\n");

    const parsed = lines.map((line) => {
      const cols = line.split(/\t|,/);

      const row = {
        code: cols[0]?.trim() ?? "",
        construction_type: cols[1]?.trim() ?? "",
        payment_date: cols[2]?.trim() ?? "",
        expected_amount: cols[3]?.trim() ?? "",
        actual_amount: cols[4]?.trim() ?? "",
        memo: cols[5]?.trim() ?? "",
      };

      return { ...row, errors: validateRow(row) };
    });

    setRows(parsed);
  };

  // rawText가 바뀔 때 parseExcel 실행 (애니메이션 문제 해결 핵심)
  useEffect(() => {
    if (rawText.trim() === "") {
      setRows([]);
      return;
    }
    parseExcel(rawText);
  }, [rawText]);

  const handlePaste = (e) => {
    const text = e.target.value;
    setRawText(text); // parseExcel 제거 → 애니메이션 정상 작동
  };

  const handleSubmit = async () => {
    try {
      const hasError = rows.some((r) => Object.keys(r.errors).length > 0);
      if (hasError) {
        alert("오류가 있는 항목이 있어 저장할 수 없습니다.");
        return;
      }

      for (const row of rows) {
        const formData = new FormData();
        Object.entries(row).forEach(([k, v]) => {
          if (k !== "errors") formData.append(k, v ?? "");
        });
        await createCost(formData);
      }

      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      console.error("일괄 등록 실패:", err);
      alert("일괄 저장 중 오류 발생");
    }
  };
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-2xl p-6">
        <h2 className="text-lg font-semibold mb-4">기성 일괄 등록</h2>
        <div className="mb-4 p-3 border rounded-lg bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-800">
              엑셀 복사 → 붙여넣기 예시
            </p>

            {/* 다시보기 버튼 */}
            <button
              onClick={restartExample}
              className="px-3 py-1 text-xs bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              다시보기
            </button>
          </div>

          {/* 애니메이션: key가 바뀌면 처음부터 재생됨 */}
          <ExcelDemoAnimation key={demoKey} active={true} />

          <p className="text-xs text-gray-500 mt-2">
            실제 사용 시 엑셀에서 데이터를 복사하여 아래 입력창에 붙여넣기 하면
            됩니다.(숫자는 "," 빼주세요)
          </p>
        </div>

        {/* 입력 텍스트 영역 */}
        <textarea
          className="w-full h-40 border rounded-md p-2"
          placeholder="엑셀 데이터를 그대로 붙여넣기 하세요
          (순서: 코드, 공종, 지불날짜, 계획 기성 금액, 실제 기성 금액, 비고)"
          value={rawText}
          onChange={handlePaste}
        />

        {/* 미리보기 */}
        {rows.length > 0 && (
          <div className="mt-4 border rounded-md overflow-auto max-h-60">
            <table className="w-full text-sm min-w-max">
              <thead className="bg-gray-100">
                <tr>
                  <th>코드</th>
                  <th>공종</th>
                  <th>지불일</th>
                  <th>계획금액</th>
                  <th>실제금액</th>
                  <th>메모</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className={r.errors.code ? "bg-red-100" : ""}>
                      {r.code}
                      {r.errors.code && (
                        <div className="text-red-500 text-xs">
                          {r.errors.code}
                        </div>
                      )}
                    </td>

                    <td
                      className={r.errors.construction_type ? "bg-red-100" : ""}
                    >
                      {r.construction_type}
                      {r.errors.construction_type && (
                        <div className="text-red-500 text-xs">
                          {r.errors.construction_type}
                        </div>
                      )}
                    </td>

                    <td className={r.errors.payment_date ? "bg-red-100" : ""}>
                      {r.payment_date}
                      {r.errors.payment_date && (
                        <div className="text-red-500 text-xs">
                          {r.errors.payment_date}
                        </div>
                      )}
                    </td>

                    <td
                      className={r.errors.expected_amount ? "bg-red-100" : ""}
                    >
                      {r.expected_amount}
                      {r.errors.expected_amount && (
                        <div className="text-red-500 text-xs">
                          {r.errors.expected_amount}
                        </div>
                      )}
                    </td>

                    <td className={r.errors.actual_amount ? "bg-red-100" : ""}>
                      {r.actual_amount}
                      {r.errors.actual_amount && (
                        <div className="text-red-500 text-xs">
                          {r.errors.actual_amount}
                        </div>
                      )}
                    </td>

                    <td>{r.memo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 버튼 */}
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn-cancel">
            취소
          </button>
          <button onClick={handleSubmit} className="btn-create">
            저장
          </button>
        </div>
      </div>
    </div>
  );
};

export default CostBulkNew;
